import { yupResolver } from "@hookform/resolvers/yup";
import { type AlertRootProps, Button, cn, OTPInput, OTPRoot } from "@pharaday/lighthouse";
import { useEffect, useRef } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";

import useLogin from "../../apis/useLogin";
import { useRequestMagicLink } from "../../apis/useRequestMagicLink";
import { RequestCodeAgainButton } from "../../components";
import { StepInputEmailOTPSchema } from "./StepInputEmailOTP.schema";

export interface StepInputEmailOTPProps {
  email: string;
  onContinue: () => void;
  onChangeEmail: () => void;
  onAlert: (alert?: { type: AlertRootProps["type"]; message: string }) => void;
}

const StepInputEmailOTP = ({ email, onContinue, onChangeEmail, onAlert }: StepInputEmailOTPProps) => {
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const { mutate: login, isSuccess, isPending, data } = useLogin();
  const { mutateAsync: requestMagicLink } = useRequestMagicLink();

  const methods = useForm<StepInputEmailOTPSchema>({
    resolver: yupResolver(StepInputEmailOTPSchema),
    defaultValues: {
      email,
      code: "",
    },
  });

  const {
    setValue,
    watch,
    handleSubmit,
    formState: { isDirty, isValid, errors },
    setError,
    reset,
  } = methods;

  const code = watch("code");

  const handleChange = (value: string) => {
    setValue("code", value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onSubmit: SubmitHandler<StepInputEmailOTPSchema> = async (data: StepInputEmailOTPSchema) =>
    login({
      code: data.code,
      email: data.email,
      type: "with-magic-link",
    });

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    if (data?.success) {
      onContinue();
    } else {
      setError("code", { type: "server-error" });
    }
  }, [isSuccess, data, onContinue, setError]);

  const isServerInvalidCode = errors.code?.type === "server-error";
  const isRedirecting = isSuccess && data?.success === true;
  const isLoading = isPending || isRedirecting;

  const handlePaste = () => {
    setTimeout(() => submitButtonRef.current?.click(), 50);
  };

  return (
    <FormProvider {...methods}>
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <h3 className="mb-2 text-base font-bold">Login with OTP</h3>
          <p className="text-sm">
            We sent a login code to <span className="font-bold">{email}</span>. Please enter the code in the field
            below.
          </p>
        </div>

        <div>
          <OTPRoot
            className="justify-between"
            onChange={handleChange}
            value={code}
            disabled={isLoading}
            isInvalid={isServerInvalidCode}
            onPaste={handlePaste}
          >
            <OTPInput />
            <OTPInput />
            <OTPInput />
            <OTPInput />
            <OTPInput />
            <OTPInput />
          </OTPRoot>

          {isServerInvalidCode && (
            <span className="mt-1 text-sm text-error-50">The verification code that you entered is incorrect</span>
          )}
        </div>

        <div className="flex flex-col gap-3 text-sm">
          <p className="flex gap-2">
            Didn&apos;t receive the code?
            <RequestCodeAgainButton
              onRequest={async () => {
                await requestMagicLink(email);
                reset();
              }}
              onAlert={onAlert}
              successMessage="We sent you a new login code"
            />
          </p>
          <p className="flex gap-2">
            Wrong email address?
            <Button
              color="primary"
              variant="link"
              className="underline"
              type="button"
              disabled={isLoading}
              onClick={onChangeEmail}
            >
              Use a different email
            </Button>
          </p>
        </div>

        <Button
          ref={submitButtonRef}
          className={cn("mt-4", isLoading && "cursor-wait!")}
          size="lg"
          isLoading={isLoading}
          disabled={!isValid || !isDirty}
          type="submit"
        >
          Continue
        </Button>
      </form>
    </FormProvider>
  );
};

export default StepInputEmailOTP;
